// @ts-nocheck
import React, { useState, useMemo } from "react";
import { plateStockType } from "@apv-erp/status";
import { useNavigate } from "@tanstack/react-router";
import {
  MoreHorizontal,
  Trash2,
  ArrowUpRight,
  Link as LinkIcon,
  Plus,
  Download,
  Loader2,
  AlertCircle,
  History,
  Check,
  X,
} from "lucide-react";
import CTPDispatchBoard from "./CTPDispatchBoard";
import { Button } from "@/components/ui/button";
import { ProductionThumbnailCell } from "@/components/shared/ProductionThumbnailCell";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn, formatDate } from "@/lib/utils";
import { Checkbox } from "@/components/ui/checkbox";
import { getStatusConfig } from "@/config/status-registry";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DataTableRow,
  DataTableRowCheckbox,
  DataTableSelectAllCheckbox,
} from "@/components/shared/DataTableRow";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { trpcClient } from "@/lib/trpc";
import { useQueryClient, useMutation } from "@tanstack/react-query";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { PageFilterTags } from "@/components/shared/PageFilterTags";
import { type StatusSidebarItem } from "@/components/shared/StatusSidebar";
import { ToolingStatus } from "@/config/status-registry";
import { useExcelExport } from "@/hooks/useExcelExport";
import type { ExcelColumnDef } from "@/lib/excel.utils";

/** Cột Excel bản kẽm (05/11/2026): đủ tên, màu, khổ, tái bản, số lượng như trên màn; trạng thái xuất tên hiển thị */
const EXPORT_COLUMNS: ExcelColumnDef[] = [
  { key: "plateId", label: "Mã bài in", width: 16 },
  { key: "name", label: "Tên bản kẽm", width: 25 },
  { key: "productName", label: "Sản phẩm", width: 25 },
  { key: "colors", label: "Màu sắc", width: 18, format: (v) => (Array.isArray(v) ? v.join(", ") : v || "") },
  { key: "size", label: "Khổ kẽm", width: 14 },
  { key: "quantity", label: "Số bản", width: 10, format: "number" },
  { key: "version", label: "Tái bản", width: 10 },
  { key: "status", label: "Trạng thái", width: 16, format: (v) => getStatusConfig("PlateStatus", v)?.label || v || "" },
];
import { useRowSelection } from "@/hooks/useRowSelection";
import { useRoleAccess } from "@/hooks/useRoleAccess";
import { useConfirm } from "@/providers/ConfirmProvider";
import { executeBulkAction } from "@/utils/bulkActionUtils";
import {
  ListPageShell,
  useServerListFilter,
} from "@/components/shared/ListPageShell";
import {
  InlineFilterRow,
  type FilterField,
  CHECKBOX_COL_NULL,
} from "@/components/shared/InlineFilterRow";
import { SortableHeader } from "@/components/shared/SortableHeader";
import {
  BulkActionBar,
  type BulkAction,
} from "@/components/shared/BulkActionBar";
import { EMS_TAB_BAR, emsTabClass } from "@/components/ui/ems-tabs";

const PLATE_FILTER_FIELDS: (FilterField | null)[] = [
  CHECKBOX_COL_NULL,
  { key: "plateId", label: "Lọc mã bài in...", type: "text" },
  { key: "productName", label: "Lọc sản phẩm / quy trình...", type: "text" },
  { key: "color", label: "Lọc màu sắc...", type: "text" },
  { key: "specs", label: "Lọc quy cách...", type: "text" },
  null,
  null,
  null,
];

const PLATE_ACTIVE_FILTER_FIELDS: FilterField[] = PLATE_FILTER_FIELDS.filter(
  (f): f is FilterField => Boolean(f && f.key),
);

const getStatusItems = (): StatusSidebarItem[] => [
  {
    key: "PENDING",
    label:
      (ToolingStatus && ToolingStatus.PENDING && ToolingStatus.PENDING.label) ||
      "Chờ nhập",
    color:
      (ToolingStatus && ToolingStatus.PENDING && ToolingStatus.PENDING.color) ||
      "bg-amber-100 text-amber-700",
    dotColor:
      (ToolingStatus &&
        ToolingStatus.PENDING &&
        ToolingStatus.PENDING.dotColor) ||
      "bg-amber-500",
  },
  {
    key: "AVAILABLE",
    label:
      (ToolingStatus &&
        ToolingStatus.AVAILABLE &&
        ToolingStatus.AVAILABLE.label) ||
      "Đang dùng",
    color:
      (ToolingStatus &&
        ToolingStatus.AVAILABLE &&
        ToolingStatus.AVAILABLE.color) ||
      "bg-emerald-100 text-emerald-700",
    dotColor:
      (ToolingStatus &&
        ToolingStatus.AVAILABLE &&
        ToolingStatus.AVAILABLE.dotColor) ||
      "bg-emerald-500",
  },
  {
    key: "DAMAGED",
    label:
      (ToolingStatus && ToolingStatus.DAMAGED && ToolingStatus.DAMAGED.label) ||
      "Hỏng",
    color:
      (ToolingStatus && ToolingStatus.DAMAGED && ToolingStatus.DAMAGED.color) ||
      "bg-rose-100 text-rose-700",
    dotColor:
      (ToolingStatus &&
        ToolingStatus.DAMAGED &&
        ToolingStatus.DAMAGED.dotColor) ||
      "bg-rose-500",
  },
  {
    key: "CANCELLED",
    label:
      (ToolingStatus &&
        ToolingStatus.CANCELLED &&
        ToolingStatus.CANCELLED.label) ||
      "Đã hủy",
    color:
      (ToolingStatus &&
        ToolingStatus.CANCELLED &&
        ToolingStatus.CANCELLED.color) ||
      "bg-stone-100 text-stone-600",
    dotColor:
      (ToolingStatus &&
        ToolingStatus.CANCELLED &&
        ToolingStatus.CANCELLED.dotColor) ||
      "bg-stone-400",
  },
];

const getColorStyle = (color: string) => {
  const c = color.trim().toUpperCase();
  if (c === "C" || c.includes("CYAN"))
    return "bg-cyan-100 text-cyan-700 border-cyan-200 font-bold shadow-sm";
  if (c === "M" || c.includes("MAGENTA"))
    return "bg-pink-100 text-pink-700 border-pink-200 font-bold shadow-sm";
  if (c === "Y" || c.includes("YELLOW"))
    return "bg-yellow-100 text-yellow-700 border-yellow-200 font-bold shadow-sm";
  if (c === "K" || c.includes("BLACK") || c.includes("KEY"))
    return "bg-slate-900 text-slate-50 border-slate-700 font-bold shadow-sm";
  return "bg-slate-100 text-slate-600 border-slate-200";
};

export default function PlatesPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<"dispatch" | "list">("dispatch");
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [plateToDelete, setPlateToDelete] = useState<{ id: string; name: string } | null>(null);

  const deleteMutation = useMutation({
    mutationFn: (id: string) => trpcClient.technical.plates.delete.mutate({ id }),
    onSuccess: () => {
      toast.success("Đã xóa bản kẽm thành công");
      queryClient.invalidateQueries({ queryKey: ["plates"] });
      setPlateToDelete(null);
    },
    onError: (err: any) => {
      toast.error(err.message || "Không thể xóa bản kẽm đã có lịch sử ghi hoặc in");
    },
  });

  const updateStatusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      trpcClient.technical.plates.updateStatus.mutate({ id, status }),
    onSuccess: () => {
      toast.success("Đã cập nhật trạng thái bản kẽm");
      queryClient.invalidateQueries({ queryKey: ["plates"] });
    },
    onError: (err: any) => {
      toast.error(err.message || "Lỗi cập nhật trạng thái bản kẽm");
    },
  });

  const {
    items,
    isLoading,
    filterValues,
    showFilter,
    currentPage,
    pageSize,
    totalItems,
    statusFilter,
    onStatusChange,
    onFilterChange,
    onPageChange,
    onPageSizeChange,
    onToggleFilter,
    onClearAll,
    sortField,
    sortOrder,
    handleSort,
  } = useServerListFilter<any>({
    queryKey: "plates",
    queryFn: (params) => trpcClient.technical.plates.list.query(params) as any,
    defaultLimit: 500, // Master data loads large batch
  });

  const displayItems = useMemo(() => {
    return items.filter((item: any) => {
      const code = (item.plateId || item.id || "").toLowerCase();
      if (filterValues.plateId && !code.includes(filterValues.plateId.toLowerCase())) return false;
      const prod = (item.productName || item.product?.productCode || item.product?.name || "").toLowerCase();
      if (filterValues.productName && !prod.includes(filterValues.productName.toLowerCase())) return false;
      if (filterValues.color) {
        const clr = (item.colors || []).join(" ").toLowerCase();
        if (!clr.includes(filterValues.color.toLowerCase())) return false;
      }
      if (filterValues.specs) {
        const specStr = `${item.plateSize || ""} ${item.machineType || ""} ${item.screenRuling || ""}`.toLowerCase();
        if (!specStr.includes(filterValues.specs.toLowerCase())) return false;
      }
      return true;
    });
  }, [items, filterValues]);

  const rowIds = displayItems.map((p: any) => p.plateId || p.id);
  const {
    selectedIds,
    selectionMode,
    toggleSelect,
    handleRowSelect,
    toggleSelectAll,
    handleRowMouseDown,
    handleRowMouseUp,
    handleRowMouseLeave,
    wasLongPress,
    allSelected,
    selectedCount,
    clearSelection,
  } = useRowSelection(rowIds);

  const { handleExport, isExporting } = useExcelExport({
    filename: "danh_sach_bai_in",
    columns: EXPORT_COLUMNS,
    serverQuery: async (filters) => {
      // Xuất Excel bản kẽm: route đúng là technical.plates (trước gọi plates.list không tồn tại nên xuất lỗi)
      const res = await (trpcClient as any).technical.plates.list.query({
        ...filters,
        status: statusFilter === "ALL" ? undefined : statusFilter,
        page: 1,
        limit: 5000,
      });
      return (res as any)?.items || (res as any) || [];
    },
    filterValues,
    localData: displayItems,
    selectedIds: Array.from(selectedIds),
    idKey: "plateId",
  });

  const { hasPermission } = useRoleAccess();
  const confirmDialog = useConfirm();
  const getBulkActions = (): BulkAction[] => {
    if (!selectedCount) return [];
    const actions: BulkAction[] = [
      {
        label: "Xuất Excel",
        icon: <Download className="w-3.5 h-3.5" />,
        onClick: () => {
          handleExport();
          clearSelection();
        },
      },
    ];
    if (hasPermission("plates", "delete")) {
      const byKey = new Map(displayItems.map((p: any) => [p.plateId || p.id, p.id || p.plateId]));
      actions.push({
        label: `Xoá (${selectedCount})`,
        icon: <Trash2 className="w-3.5 h-3.5" />,
        variant: "destructive",
        onClick: () =>
          executeBulkAction({
            confirmFn: confirmDialog,
            selectedIds: Array.from(selectedIds).map((k: any) => byKey.get(k) || k),
            actionFn: async (id) => trpcClient.technical.plates.delete.mutate({ id: id as string }),
            actionName: "xóa",
            itemType: "bản kẽm",
            onSuccess: () => {
              clearSelection();
              queryClient.invalidateQueries({ queryKey: ["plates"] });
            },
            requireConfirm: true,
            confirmMessage: `Xóa ${selectedCount} bản kẽm đã chọn? Bản kẽm đã có lịch sử ghi/in sẽ không xóa được.`,
          }),
      });
    }
    return actions;
  };

  const filterTags = React.useMemo(() => {
    return getStatusItems().map((item) => ({
      ...item,
      count: 0, // In server-side mode, counts come from meta if implemented
    }));
  }, []);

  return (
    <>
      <div className="px-4 pt-2 bg-white">
        <div role="tablist" aria-label="Kẽm CTP" className={EMS_TAB_BAR}>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "dispatch"}
            onClick={() => setActiveTab("dispatch")}
            className={emsTabClass(activeTab === "dispatch")}
          >
            <span>🖨️ Điều Phối Kẽm</span>
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "list"}
            onClick={() => setActiveTab("list")}
            className={emsTabClass(activeTab === "list")}
          >
            <span>📋 Danh Mục Bản Kẽm</span>
          </button>
        </div>
      </div>

      {activeTab === "dispatch" ? (
        <div className="p-4">
          <CTPDispatchBoard />
        </div>
      ) : (
        <>
          <ListPageShell
        trashModule="plates"
        unitLabel="Bài in / Chế bản"
        currentPage={currentPage}
        pageSize={pageSize}
        totalItems={totalItems}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        onToggleFilter={onToggleFilter}
        onClearAll={onClearAll}
        showFilter={showFilter}
        filterValues={filterValues}
        filterFields={PLATE_ACTIVE_FILTER_FIELDS}
        headerFilters={
          <div className="flex items-center gap-2">
            <Select
              value={statusFilter ? (Array.isArray(statusFilter) ? statusFilter[0] : (statusFilter as string)) : "ALL"}
              onValueChange={(val) => onStatusChange(val === "ALL" ? "" : val)}
            >
              <SelectTrigger className="h-8 w-[160px] text-xs bg-white">
                <SelectValue placeholder="Trạng thái bài in" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">Tất cả trạng thái</SelectItem>
                {["AVAILABLE", "PENDING", "DAMAGED", "CANCELLED"].map((key) => {
                  const cfg = getStatusConfig("PlateStatus", key);
                  return (
                    <SelectItem key={key} value={key}>
                      {cfg.label}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>
        }
        actions={
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              className="h-8 text-xs bg-white"
              onClick={() => handleExport()}
              disabled={isExporting}
            >
              <Download className="mr-2 h-3 w-3" />
              {isExporting ? "Đang xuất..." : "Xuất Excel"}
            </Button>
            <Button
              size="sm"
              className="h-8 bg-indigo-600 hover:bg-indigo-700 text-white text-xs"
              onClick={() => navigate({ to: "/plates/create" })}
            >
              <Plus className="mr-2 h-3 w-3" /> Thêm bài in
            </Button>
          </div>
        }
      >
        <Table className={cn(selectionMode && "apv-select-mode")}>
          <TableHeader className="apv-table-header">
            <TableRow>
              <TableHead className={cn("apv-col-checkbox w-10 text-center")}>
                <DataTableSelectAllCheckbox
                  rowSelection={
                    { allSelected, toggleSelectAll, selectionMode } as any
                  }
                  asChild
                />
              </TableHead>
              <SortableHeader
                label="Mã bài in"
                field="plateId"
                currentField={sortField}
                currentOrder={sortOrder}
                onSort={handleSort}
                className="apv-col-code w-[120px]"
              />
              <SortableHeader
                label="Sản phẩm / Quy trình"
                field="productName"
                currentField={sortField}
                currentOrder={sortOrder}
                onSort={handleSort}
              />
              <TableHead className="font-semibold text-[11px] uppercase py-3">
                Màu sắc
              </TableHead>
              <TableHead className="font-semibold text-[11px] uppercase py-3 w-[150px]">
                Quy cách &amp; Kẽm CTP
              </TableHead>
              <TableHead className="font-semibold text-[11px] uppercase py-3 w-[140px]">
                Tái bản Prepress
              </TableHead>
              <TableHead className="font-semibold text-[11px] uppercase py-3 w-[120px]">
                File Kỹ thuật
              </TableHead>
              <TableHead className="w-[50px] apv-col-actions"></TableHead>
            </TableRow>
            {showFilter && (
              <InlineFilterRow
                columns={PLATE_FILTER_FIELDS as any}
                filterFields={PLATE_ACTIVE_FILTER_FIELDS}
                filterValues={filterValues}
                onFilterChange={onFilterChange}
              />
            )}
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array.from({ length: 8 }).map((_, i) => (
                <TableRow key={i}>
                  <TableCell className={cn("px-4 apv-col-checkbox")} />
                  {Array.from({ length: 7 }).map((_, j) => (
                    <TableCell key={j}>
                      <Skeleton className="h-4 w-14" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : displayItems.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={8}
                  className="text-center py-12 text-slate-400"
                >
                  Không có dữ liệu bài in
                </TableCell>
              </TableRow>
            ) : (
              displayItems.map((plate: any) => {
                const plateKey = plate.plateId || plate.id;
                return (
                  <DataTableRow
                    key={plateKey}
                    id={plateKey}
                    rowSelection={
                      {
                        selectedIds,
                        handleRowMouseDown,
                        handleRowMouseUp,
                        handleRowMouseLeave,
                        wasLongPress,
                        selectionMode,
                        handleRowSelect,
                      } as any
                    }
                    onClick={(e) => {
                      if (selectionMode) return;
                      navigate({
                        to: "/plates/$plateId",
                        params: { plateId: plateKey },
                      });
                    }}
                  >
                    <TableCell
                      className={cn("px-4 apv-col-checkbox")}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <DataTableRowCheckbox
                        id={plateKey}
                        rowSelection={{ selectedIds, toggleSelect } as any}
                        asChild
                      />
                    </TableCell>
                    <TableCell className="apv-col-code py-1.5">
                      <div className="flex flex-col gap-1.5 items-start">
                        <span className="font-bold text-indigo-600 text-[13px] leading-none group-hover:underline">
                          {plateKey}
                        </span>
                        <StatusBadge
                          type="ToolingStatus"
                          value={plate.status}
                        />
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                      <ProductionThumbnailCell
                        imageUrl={plate.product?.image}
                        layoutUrl={plate.image}
                        title={plate.productName || undefined}
                        subtitle={plate.plateId}
                        type="product"
                        size={34}
                      />
                      <div className="flex flex-col gap-1 min-w-0">
                        <div className="font-semibold text-[12px] text-slate-900 leading-tight uppercase truncate max-w-[250px]">
                          {plate.productName || "—"}
                        </div>
                        <div className="text-[10px] font-bold text-slate-500 uppercase tracking-tighter leading-none">
                          {plate.product?.productCode ||
                            plate.productName || "—"}
                        </div>
                      </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {(plate.colors || []).map((c: string, idx: number) => (
                          <Badge
                            key={idx}
                            variant="outline"
                            className={cn(
                              "text-[9px] px-1.5 py-0 h-4 border leading-none",
                              getColorStyle(c),
                            )}
                          >
                            {c}
                          </Badge>
                        ))}
                        {(!plate.colors || plate.colors.length === 0) && (
                          <span className="text-slate-300 italic text-[10px]">
                            Chưa set màu
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-1">
                        <div className="flex items-center gap-1.5">
                          <span className="font-mono font-bold text-slate-800 text-[11px]">
                            {plateStockType(plate.size)}
                          </span>
                          <span className="text-[10px] text-indigo-600 bg-indigo-50 border border-indigo-200 px-1 py-0.2 rounded font-medium">
                            {(plate.colors || []).length || 4} lá kẽm
                          </span>
                        </div>
                        <div className="text-[10px] text-slate-500">
                          Thermal CTP &bull; Tiêu hao 1 lần
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-1 text-[11px]">
                        <span className={cn(
                          "px-1.5 py-0.5 rounded border text-[10px] font-semibold w-fit",
                          plate.designFileUrl || plate.ctpFileUrl
                            ? "bg-emerald-50 text-emerald-800 border-emerald-300"
                            : "bg-slate-50 text-slate-500 border-slate-200"
                        )}>
                          {plate.designFileUrl || plate.ctpFileUrl ? "✓ File sẵn sàng" : "Chưa có file"}
                        </span>
                        <span className="text-[10px] text-slate-500 truncate max-w-[130px]">
                          {plate.printerType || "Komori / Heidelberg"}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-1 text-[10px] text-slate-500 font-medium">
                        <div className="flex items-center gap-1 leading-none">
                          {plate.designFileUrl ? (
                            <Check className="w-3 h-3 text-emerald-500" />
                          ) : (
                            <X className="w-3 h-3 text-rose-500" />
                          )}{" "}
                          File AI
                        </div>
                        <div className="flex items-center gap-1 leading-none">
                          {plate.ctpFileUrl ? (
                            <Check className="w-3 h-3 text-emerald-500" />
                          ) : (
                            <X className="w-3 h-3 text-rose-500" />
                          )}{" "}
                          File CTP
                        </div>
                      </div>
                    </TableCell>
                    <TableCell
                      className="px-4 text-right apv-col-actions"
                      onClick={(e: React.MouseEvent) => e.stopPropagation()}
                    >
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 hover:bg-slate-100 text-slate-500 hover:text-indigo-600"
                            title="Thao tác"
                          >
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                          align="end"
                          className="w-48 z-[9999]"
                        >
                          <DropdownMenuLabel className="text-xs text-slate-500">
                            Thao tác bản kẽm
                          </DropdownMenuLabel>
                          <DropdownMenuItem
                            onClick={() =>
                              updateStatusMutation.mutate({
                                id: plate.id || plateKey,
                                status: plate.status === "DAMAGED" ? "READY" : "DAMAGED",
                              })
                            }
                          >
                            <AlertCircle className="mr-2 h-4 w-4 text-amber-500" />{" "}
                            {plate.status === "DAMAGED" ? "Kích hoạt lại" : "Báo hỏng / Hủy kẽm"}
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            className="text-rose-600 focus:text-rose-700"
                            onClick={() =>
                              setPlateToDelete({
                                id: plate.id || plateKey,
                                name: plate.name || plate.plateId || plateKey,
                              })
                            }
                          >
                            <Trash2 className="mr-2 h-4 w-4" /> Xóa bản kẽm
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </DataTableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </ListPageShell>

      <BulkActionBar
        selectedCount={selectedCount}
        onClearSelection={clearSelection}
        actions={getBulkActions()}
      />
        </>
      )}

      <AlertDialog open={!!plateToDelete} onOpenChange={(open) => !open && setPlateToDelete(null)}>
        <AlertDialogContent className="z-[10000]">
          <AlertDialogHeader>
            <AlertDialogTitle>Xác nhận xóa bản kẽm?</AlertDialogTitle>
            <AlertDialogDescription>
              Bạn có chắc chắn muốn xóa bản kẽm <strong>{plateToDelete?.name}</strong>?
              Hành động này không thể hoàn tác nếu bản kẽm chưa từng ghi hoặc chạy máy.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Hủy</AlertDialogCancel>
            <AlertDialogAction
              className="bg-rose-600 hover:bg-rose-700 text-white"
              onClick={() => plateToDelete && deleteMutation.mutate(plateToDelete.id)}
            >
              {deleteMutation.isPending ? "Đang xóa..." : "Xác nhận xóa"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
